import { useState, useEffect, useCallback } from 'react';
import { GamesSidebar } from './GamesSidebar';
import { LeaderboardPanel } from './LeaderboardPanel';
import { UserProfile } from './UserProfile';
import { AdminPanel } from './AdminPanel';
import { RoomLobby } from './games/RoomLobby';
import { GameRoom } from './games/GameRoom';
import { VerifyEmailBanner } from './auth/VerifyEmailBanner';
import { NotificationsBell } from './NotificationsBell';
import { getToken } from '@/utils/auth';
import { logger } from '@/utils/logger';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = ({ user, onLogout, onAccountDeleted }) => {
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);

  const fetchGames = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/games`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGames(response.data);
    } catch (error) {
      logger.error('Failed to fetch games:', error);
    }
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data);
    } catch (error) {
      logger.error('Failed to fetch leaderboard:', error);
    }
  }, []);

  useEffect(() => {
    fetchGames();
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 10000);
    return () => clearInterval(interval);
  }, [fetchGames, fetchLeaderboard]);

  const handleViewProfile = useCallback((userId) => {
    setSelectedUserId(userId);
    setIsProfileOpen(true);
  }, []);

  const handleCloseProfile = useCallback(() => {
    setIsProfileOpen(false);
    setSelectedUserId(null);
  }, []);

  const handleSelectGame = useCallback((game) => {
    setSelectedGame(game);
    setCurrentRoom(null);
  }, []);

  const handleRoomJoined = useCallback((room) => {
    setCurrentRoom(room);
  }, []);

  const handleInviteAccepted = useCallback((room) => {
    // Receiver already lands here; sender gets this via WS and auto-joins
    setSelectedGame(null);
    setCurrentRoom(room);
  }, []);

  const handleLeaveRoom = useCallback(() => {
    setCurrentRoom(null);
  }, []);

  const handleGameEnd = useCallback(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-white" data-testid="dashboard">
      <VerifyEmailBanner user={user} />
      <div className="h-12 border-b border-[#E4E4E7] bg-white flex items-center justify-end px-4 gap-2" data-testid="dashboard-topbar">
        <NotificationsBell user={user} onInviteAccepted={handleInviteAccepted} />
      </div>
      <div className="flex flex-1 overflow-hidden">
      <GamesSidebar
        games={games}
        selectedGame={selectedGame}
        onSelectGame={handleSelectGame}
        user={user}
        onLogout={onLogout}
        onViewProfile={handleViewProfile}
        onOpenAdmin={() => setIsAdminOpen(true)}
      />
      
      {currentRoom ? (
        <GameRoom 
          room={currentRoom} 
          user={user}
          onLeaveRoom={handleLeaveRoom}
          onGameEnd={handleGameEnd}
        />
      ) : selectedGame ? (
        <RoomLobby 
          game={selectedGame}
          onRoomJoined={handleRoomJoined}
          user={user}
        />
      ) : (
        <div className="flex-1 flex items-center justify-center bg-[#FAFAFA]" data-testid="empty-state">
          <div className="text-center">
            <p className="text-lg font-medium text-[#09090B] mb-2">Welcome to DashLink Gaming!</p>
            <p className="text-sm text-[#71717A]">Select a game from the sidebar to get started</p>
          </div>
        </div>
      )}
      
      <LeaderboardPanel
        leaderboard={leaderboard}
        currentUser={user}
        onViewProfile={handleViewProfile}
      />

      <UserProfile
        userId={selectedUserId}
        isOpen={isProfileOpen}
        onClose={handleCloseProfile}
        currentUserId={user.id}
        onAccountDeleted={onAccountDeleted}
      />

      {user.is_admin && (
        <AdminPanel
          isOpen={isAdminOpen}
          onClose={() => setIsAdminOpen(false)}
          onGamesUpdated={fetchGames}
        />
      )}
      </div>
    </div>
  );
};
